import { formatDate } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ProductionJobStatus } from '../../../models/review/production-job.model';
import { ProductionJobView } from '@models/ui/production-job-view.model';
import { NotificationManager } from '@managers/notification.manager';

export class SelectedRangeEvent {
	productionJob: ProductionJobView
	lastRunProductionJob: ProductionJobView
}

@Component({
    selector: 'lex-bates-range-select',
    templateUrl: './bates-range-select.component.html',
    styleUrls: ['./bates-range-select.component.scss'],
    standalone: false
})
export class BatesRangeSelectComponent implements OnChanges {

	@Input() productionJobs: ProductionJobView[];
	@Output() selectedRangeEvent: EventEmitter<SelectedRangeEvent> = new EventEmitter<SelectedRangeEvent>();
	@Input() isCreateMode: boolean = false;

	public displayColumns = ['produced', 'withheld', 'dateTimeRun'];
	public batesRangeGroups: Map<string, ProductionJobView[]> = new Map<string, ProductionJobView[]>();

	public expandAll: boolean = true;

	constructor(
		private notificationManager: NotificationManager,
	) { }

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.productionJobs && this.productionJobs) {
			this.productionJobs.forEach(j => {
				if (!this.batesRangeGroups.has(j.batesBasePrefix)) {
					const jobs: ProductionJobView[] = [];
					this.batesRangeGroups.set(j.batesBasePrefix, jobs);
				}
				this.batesRangeGroups.get(j.batesBasePrefix).push(j);
			});
			this.batesRangeGroups.forEach(jobs =>
				jobs.sort((a: ProductionJobView, b: ProductionJobView) => {
					if (a.batesBaseNumberStart == b.batesBaseNumberStart)
						return a.productionJobId > b.productionJobId ? 1 : -1;
					return a.batesBaseNumberStart > b.batesBaseNumberStart ? 1 : -1;
				}));
			this.expandAll = this.batesRangeGroups.size == 1;
		}
	}

	public selectProduction(job: ProductionJobView) {
		this.selectedRangeEvent.emit({
			productionJob: job,
			lastRunProductionJob: null
		});
	}

	public getBatesRange(job: ProductionJobView): string {
		if (job.status == ProductionJobStatus.PreRun || job.status == ProductionJobStatus.NeedValidation)
			return `${job.batesBasePrefix} ${job.batesBaseNumberStart.toString().padStart(job.batesBaseNumberDigits, '0')} -`;

		return job.batesRangeProduction;
	}

	public getDateTimeRunString(job: ProductionJobView): string {
		if (!job) return;

		if (job.isImportProductionJob) return '[Imported Job]';

		if (job.status == ProductionJobStatus.NeedValidation || job.status == ProductionJobStatus.PreRun)
			return '[Not Run]';

		return formatDate(new Date(job.dateTimeRun), 'M/d/yyyy h:mm:ss a', 'en-US');
	}

	public continueWithExistingRange(prefix: string) {
		let job = new ProductionJobView();
		const lastRunProd = this.getProductionByPrefixAndMaxEndRange(prefix);
		if (!lastRunProd) {
			job = this.newProductionDefault(prefix);
		}
		else {
			const endOfBatesRange = this.getBatesRangeEnd(lastRunProd);
			job.title = `Production ${formatDate(new Date(), 'MM-dd-yyyy', 'en-US')}`;
			job.batesBasePrefix = prefix;
			job.batesBaseNumberStart = endOfBatesRange + 1;
			job.batesBaseNumberDigits = lastRunProd.batesBaseNumberDigits;
			job.customDesignation = lastRunProd.customDesignation;
			job.isBatesStampPages = lastRunProd.isBatesStampPages;
			job.isCreateTiff = lastRunProd.isCreateTiff;
			job.isIncludeNativeEmails = lastRunProd.isIncludeNativeEmails;
			job.level1FieldId = lastRunProd.level1FieldId;
			job.sortLevel1 = lastRunProd.sortLevel1;
			job.sortLevel2 = lastRunProd.sortLevel2;
			job.sortId = lastRunProd.sortId ?? null;
		}
		this.selectedRangeEvent.emit({
			productionJob: job,
			lastRunProductionJob: lastRunProd
		});

		this.notificationManager.displaySnackBarMessage({
			message: `Production created!`,
			type: 'info'
		});
	}

	public getNextBatesString(prefix: string) {
		const lastRunProd = this.getProductionByPrefixAndMaxEndRange(prefix);
		if (!lastRunProd)
			return `${prefix} ${(1).toString().padStart(6, '0')}`;

		const endOfBatesRange = this.getBatesRangeEnd(lastRunProd);
		return `${prefix} ${(endOfBatesRange + 1).toString().padStart(lastRunProd.batesBaseNumberDigits, '0')}`;
	}

	private getProductionByPrefixAndMaxEndRange(prefix: string): ProductionJobView {
		const postRunProductions = this.batesRangeGroups.get(prefix).filter(j => j.status == ProductionJobStatus.PostRun);
		if (postRunProductions.length < 1)
			return;

		const endRangeArray: Map<string, number> = new Map<string, number>();
		postRunProductions.map(j => {
			endRangeArray.set(j.productionJobId, this.getBatesRangeEnd(j));
		});

		const kvpMax = [...endRangeArray.entries()].reduce((a, b) => b[1] > a[1] ? b : a);
		return postRunProductions.find(j => j.productionJobId == kvpMax[0]);
	}

	private newProductionDefault(prefix: string = ''): ProductionJobView {
		const job = new ProductionJobView();
		job.title = `Production ${formatDate(new Date(), 'MM-dd-yyyy', 'en-US')}`;
		job.batesBasePrefix = prefix;
		job.batesBaseNumberStart = 1;
		job.batesBaseNumberDigits = 7;
		job.isBatesStampPages = true;
		job.isCreateTiff = false;
		job.isIncludeNativeEmails = false;
		return job;
	}

	private parseEndRange(batesArray: string[], batesPrefix: string): number {
		const prefixIndex = batesArray[1].indexOf(batesPrefix);
		if (prefixIndex < 0) return 0;

		const start = prefixIndex + (batesPrefix.length + 1);
		const end = batesArray[1].length;
		const endNumber = parseInt(batesArray[1].substring(start, end));
		if (isNaN(endNumber)) return 0;

		return endNumber;
	}

	private getBatesRangeEnd(job: ProductionJobView): number {
		const producedArray = job.batesRangeProduction.split(' - ');
		const withheldArray = job.batesRangeWithheld.split(' - ');

		let withheldRangeEnd = 1;
		if (withheldArray.length > 1)
			withheldRangeEnd = this.parseEndRange(withheldArray, job.batesBasePrefix);

		let producedRangeEnd = 1;
		if (producedArray.length > 1)
			producedRangeEnd = this.parseEndRange(producedArray, job.batesBasePrefix);

		return withheldRangeEnd > producedRangeEnd ? withheldRangeEnd : producedRangeEnd;
	}

}
